import { Link } from "react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nosible/shared/components/ui/table";
import { Badge } from "@nosible/shared/components/ui/badge";
import { Button } from "@nosible/shared/components/ui/button";
import { Users, User, Building2 } from "lucide-react";
import { trpc } from "@/admin/lib/trpc";
import type { AppRouter } from "@nosible/api";
import type { inferRouterOutputs } from "@trpc/server";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type PlanSubscription = RouterOutputs["admin"]["getPlanSubscriptions"]["subscriptions"][number];

interface PlanSubscriptionsProps {
  planId: string;
}

const getStatusBadge = (status: string) => {
  switch (status) {
    case "active":
      return <Badge className="bg-neon-green text-obsidian">Active</Badge>;
    case "trialing":
      return <Badge className="bg-signal-blue">Trialing</Badge>;
    case "canceled":
      return <Badge variant="destructive">Canceled</Badge>;
    case "past_due":
      return <Badge variant="destructive">Past Due</Badge>;
    case "incomplete":
      return <Badge variant="secondary">Incomplete</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
};

export function PlanSubscriptions({ planId }: PlanSubscriptionsProps) {
  const { data, isLoading, error } = trpc.admin.getPlanSubscriptions.useQuery({ planId });

  const subscriptions = data?.subscriptions ?? [];

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-chromium-mist">
          Loading subscriptions…
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-red-400">
          Error loading subscriptions: {error.message}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Subscriptions ({data?.total ?? 0})
        </CardTitle>
        <CardDescription>
          All subscriptions on this plan
        </CardDescription>
      </CardHeader>
      <CardContent>
        {subscriptions.length === 0 ? (
          <div className="text-center py-8 text-chromium-mist">
            <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No subscriptions on this plan</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Owner</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Usage</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subscriptions.map((sub: PlanSubscription) => (
                <TableRow key={sub.id}>
                  <TableCell>
                    {sub.ownerId ? (
                      <div className="flex items-center gap-2">
                        {sub.ownerType === "organization" ? (
                          <Building2 className="h-4 w-4 text-violet-pulse" />
                        ) : (
                          <User className="h-4 w-4 text-signal-blue" />
                        )}
                        <Link
                          to={
                            sub.ownerType === "organization"
                              ? `/organizations/${sub.ownerId}`
                              : `/users/${sub.ownerId}`
                          }
                          className="text-silica hover:text-neon-green transition-colors"
                        >
                          {sub.ownerName || sub.ownerId}
                        </Link>
                      </div>
                    ) : (
                      <span className="text-chromium-mist">Unknown</span>
                    )}
                  </TableCell>
                  <TableCell>{getStatusBadge(sub.status)}</TableCell>
                  <TableCell>{sub.totalCount ?? 0}</TableCell>
                  <TableCell className="text-chromium-mist">
                    {sub.createdAt
                      ? new Date(sub.createdAt).toLocaleDateString()
                      : "—"}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={`/subscriptions/${sub.id}`}>
                          View
                        </Link>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
